import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  open: boolean;
  workerId: number;
  existingSkillIds?: number[];
  onClose: () => void;
  onSkillAdded: (skillId: number) => void;
};

interface Skill {
  id: number;
  display_name_en: string;
  display_name_es: string;
}

const AddSkillModal = ({
  open,
  workerId,
  existingSkillIds = [],
  onClose,
  onSkillAdded,
}: Props) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchSkills();
    }
  }, [open]);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosRubyClient.get('/api/admin/skills');
      const allSkills: Skill[] = response.data.data || [];
      // Filter out skills the worker already has
      const available =
        existingSkillIds.length > 0
          ? allSkills.filter((s) => !existingSkillIds.includes(s.id))
          : allSkills;
      setSkills(available);
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSkill) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Create a new skill request
      const response = await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/skill_requests`,
        {
          skill_id: selectedSkill.id,
        },
      );

      if (response.data.success) {
        // Call onSkillAdded with the skill_id to navigate to detail page
        onSkillAdded(selectedSkill.id);
      } else {
        setError(response.data.message || 'Failed to add skill');
      }
    } catch (err: any) {
      console.error('Error adding skill:', err);
      setError(
        err.response?.data?.message ||
          'Failed to add skill. It may already exist.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedSkill(null);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle>Add Skill to Worker</DialogTitle>
      <DialogContent sx={{ minHeight: '300px' }}>
        <Stack vertical gap={16} style={{ paddingTop: '16px' }}>
          {loading ? (
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Autocomplete
              options={skills}
              getOptionLabel={(option) => option.display_name_en}
              value={selectedSkill}
              onChange={(_, newValue) => setSelectedSkill(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Skill"
                  placeholder="Search for a skill..."
                  required
                  fullWidth
                />
              )}
              disabled={submitting}
              disablePortal
              fullWidth
            />
          )}

          {error && (
            <Small color="danger" style={{ marginTop: '8px' }}>
              {error}
            </Small>
          )}

          <Small color="lighter">
            This will create a new skill request for the worker. You'll be
            redirected to the skill detail page to manage requirements.
          </Small>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedSkill || submitting}
        >
          {submitting ? 'Adding...' : 'Add Skill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSkillModal;
